/**
 * Exposes performance details from performanceDetailsStore for website preview components.
 * Ensures data is fetched and returns derived display values.
 */

import { useEffect } from "react";
import { usePerformanceDetailsStore } from "@/stores/performanceDetailsStore";

/** Map l_slug to 2-letter language code (EN, HI, etc.) */
function languageSlugToCode(lSlug: string): string {
  if (!lSlug || typeof lSlug !== "string") return "";
  const slug = lSlug.trim().toLowerCase();
  const known: Record<string, string> = {
    english: "EN",
    hindi: "HI",
    bengali: "BN",
    punjabi: "PN",
    spanish: "ES",
    french: "FR",
    chinese: "ZH",
    russian: "RU",
    arabic: "AR",
    tamil: "TA",
    telugu: "TE",
    marathi: "MR",
    gujarati: "GU",
    kannada: "KN",
    malayalam: "ML",
  };
  return known[slug] ?? slug.slice(0, 2).toUpperCase();
}

export function usePerformanceDetailsForPreview() {
  const data = usePerformanceDetailsStore((s) => s.data);
  const ensureFetched = usePerformanceDetailsStore((s) => s.ensureFetched);

  useEffect(() => {
    ensureFetched();
  }, [ensureFetched]);

  const performingCount = data?.performing_members?.length ?? 0;
  const offstageCount = data?.performing_members?.length ?? data?.offstage_crew ?? 0;
  const minDur = data?.min_duration_mins;
  const maxDur = data?.max_duration_mins;
  const durationText =
    minDur != null && maxDur != null
      ? `${minDur} - ${maxDur}`
      : minDur != null
        ? String(minDur)
        : maxDur != null
          ? String(maxDur)
          : "—";
  const openToTravel =
    data?.open_to_travel != null && String(data.open_to_travel).trim() !== ""
      ? String(data.open_to_travel).trim()
      : "—";
  const languages = data?.performing_language ?? [];
  const languageCodes = languages.map((l) => ({
    ...l,
    code: languageSlugToCode(l.l_slug),
  }));

  return {
    data,
    performingCount,
    offstageCount,
    durationText,
    openToTravel,
    languages: languageCodes,
  };
}
